import MultipleSelect, {
  IMultipleSelectType,
} from '../components/MultipleSelect';
import CustomRangeSelect, {
  TCustomRangeSelectType,
} from '../components/CustomRangeSelect';
import { CustomRangeSelectButton } from '../components/CustomRangeSelect/CustomRangeSelectButton';
import {
  IDateAllCommonSelectType,
  IDateCommonSelectType,
  TComponentConfig,
} from '../types';
import {
  getMultipleSelectDefaultValue,
  getRangeSelectDefaultValue,
  getRangeSelectBackValueFunc,
  createRangeSelectOption,
  getAllDateDefaultValue,
  getMultipleSelectDefaultValueWithSpecialCase,
  getDateDefaultValue,
} from '../utils';
import { MultipleSelectButton } from '../components/MultipleSelect/MultipleSelectButton';
import { TSingleSelectType } from '../components/SingleSelect';
import ChannelLabel from '@op/components/ChannelLabel';
import React from 'react';
import MultiDateAllSelect, {
  TMultiDateAllSelect,
} from '../widgets/MultiDateAllSelect';
import GameSelect, { TGameSelectType } from '../components/GameSelect';
import { GAME_CATEGORY } from '../constants/games';
import RegionSelect, { TRegionSelectType } from '../widgets/RegionSelect';
import { countryCn, countryEn } from '@op/constants';
import MultiDateSelect, { TMultiDateSelect } from '../widgets/MultiDateSelect';

function dealwithCondOptions(condInfo: any, key: string) {
  const data = condInfo[key] || [];
  const options = data.map((item: any) => ({
    value: item.value,
    text: item.label_en,
    textCn: item.label_zh,
  }));

  return options;
}

function defaultValueFunc(filterValue: any, props: IMultipleSelectType) {
  const { options, valueKeys: key } = props;
  const list = options || [];
  return getMultipleSelectDefaultValue(
    filterValue,
    key,
    list,
    list.map((item) => item.value),
  );
}

function backValueFunc(filterValue: any, props: IMultipleSelectType) {
  const { valueKeys: key, options } = props;
  const list = (options || []).map((item) => item.value);
  const values = filterValue?.[key] || [];

  // 全选：返回空数组
  if (values.length === list.length && values.length > 0) {
    return { [key]: [] };
  }

  // 全不选：返回 ["-99999"]
  if (values.length === 0) {
    return { [key]: ['-99999'] };
  }

  // 部分选择：返回选中的值
  return { [key]: values };
}

function dateBackValueFunc(filterValue: any, props: any) {
  const { valueKeys } = props;
  const { start_time, end_time } = valueKeys;
  const result: any = {};
  if (start_time in (filterValue || {})) {
    result[start_time] = filterValue[start_time];
  }
  if (end_time in (filterValue || {})) {
    result[end_time] = filterValue[end_time];
  }
  return result;
}

/**
 * 游戏过滤器配置
 *
 * 功能说明：
 * 1. 游戏分类使用 Segment 样式平铺显示
 * 2. 游戏选择使用多选下拉组件
 * 3. 切换分类时自动过滤已选游戏，只保留属于当前分类的游戏
 * 4. 默认选中所有游戏
 */
export const ADGame: TComponentConfig<TGameSelectType> = {
  // 渲染组件
  Component: GameSelect,

  // 计算默认值
  defaultValue: (filterValue: any, props: TGameSelectType) => {
    const { valueKeys, options } = props;
    const { category, games } = valueKeys;
    return {
      [category]: GAME_CATEGORY.ALL,
      [games]: filterValue[games] || options.map((item) => item.game_id),
    };
  },

  // 返回值转换
  backValue: (filterValue: any, props: TGameSelectType) => {
    const { valueKeys, options } = props || {};
    const { category, games } = valueKeys;
    const value = filterValue?.[games] || [];

    // 全选：返回空数组
    if (value.length === options.length && value.length > 0) {
      return {
        [category]: filterValue[category],
        [games]: [],
      };
    }

    // 全不选：返回 ["-99999"]
    if (value.length === 0) {
      return {
        [category]: filterValue[category],
        [games]: ['-99999'],
      };
    }

    // 部分选择：返回选中的 games
    return {
      [category]: filterValue[category],
      [games]: value,
    };
  },
  requestKey: 'materials_config_games',
  // 组件属性
  props: {
    title: 'Games',
    titleCn: '游戏',
    valueKeys: {
      category: 'game_category',
      games: 'game_ids',
    },
    allOption: { text: 'All Games', textCn: '全部游戏', value: 'all' },
    // 注意：实际游戏选项由组件通过 getGamesByCategory 动态获取
    options: [],
    listWidth: 350,
    listHeight: 300,
    searchable: true,
    matchButtonWidth: false,
  },
};


export const ADMultiDateRange: TComponentConfig<TMultiDateSelect> = {
  Component: MultiDateSelect,
  props: {
    valueKeys: {
      start_time: 'start_time',
      end_time: 'end_time',
      date_type: 'date_type',
    },
    optionsMap: { date: { value: 'date', text: 'Active Date', text_cn: '在投日期' } },
    className: 'dbom-ad-multi-date-range',
  },
  defaultValue: (filterValue: any, props: IDateCommonSelectType) => {
    return getDateDefaultValue(filterValue, props);
  },
};

export const ADType: TComponentConfig<IMultipleSelectType> = {
  Component: MultipleSelect,
  ButtonComponent: MultipleSelectButton,
  defaultValue: defaultValueFunc,
  backValue: backValueFunc,
  condDealwith: dealwithCondOptions,
  requestKey: 'materials_config_types',
  props: {
    title: 'Type',
    titleCn: '类型',
    valueKeys: 'types',
    options: [],
    listHeight: 400,
    matchButtonWidth: false,
    maxShow: 2,
  },
};

export const ADPlatform: TComponentConfig<IMultipleSelectType> = {
  Component: MultipleSelect,
  ButtonComponent: MultipleSelectButton,
  defaultValue: defaultValueFunc,
  backValue: backValueFunc,
  condDealwith: dealwithCondOptions,
  requestKey: 'materials_config_platforms',
  props: {
    title: 'Platform',
    titleCn: '平台',
    valueKeys: 'platforms',
    options: [],
    listHeight: 400,
    matchButtonWidth: false,
    maxShow: 2,
  },
};

export const ADImpressions: TComponentConfig<TCustomRangeSelectType> = {
  Component: CustomRangeSelect,
  ButtonComponent: CustomRangeSelectButton,
  defaultValue: getRangeSelectDefaultValue,
  backValue: getRangeSelectBackValueFunc(1000 * 1000),
  props: {
    title: 'Impressions',
    titleCn: '曝光量',
    valueKeys: 'impression_number',
    addOption: createRangeSelectOption,
    unit: 'M',
    options: [
      { text: 'All', textCn: '全部', value: 'all' },
      { text: '0M-50M', textCn: '0M-50M', value: '0-50' },
      { text: '50M-100M', textCn: '50M-100M', value: '50-100' },
      { text: '100M-500M', textCn: '100M-500M', value: '100-500' },
      { text: '500M-1B', textCn: '500M-1B', value: '500-1000' },
      { text: '1B+', textCn: '1B+', value: '1000-' },
    ],
  },
};

export const ADActiveDays: TComponentConfig<TCustomRangeSelectType> = {
  Component: CustomRangeSelect,
  ButtonComponent: CustomRangeSelectButton,
  defaultValue: getRangeSelectDefaultValue,
  backValue: getRangeSelectBackValueFunc(1),
  props: {
    title: 'Active Days',
    titleCn: '活跃天数',
    valueKeys: 'days',
    addOption: createRangeSelectOption,
    unit: 'D',
    options: [
      { text: 'All', textCn: '全部', value: 'all' },
      { text: '<7', textCn: '<7', value: '0-7' },
      { text: '7-30', textCn: '7-30', value: '7-30' },
      { text: '31-90', textCn: '31-90', value: '31-90' },
      { text: '91-180', textCn: '91-180', value: '91-180' },
      { text: '>180', textCn: '>180', value: '180-' },
    ],
  },
};

export const ADChannel: TComponentConfig<TSingleSelectType> = {
  Component: MultipleSelect,
  defaultValue: (filterValue: any, props: TSingleSelectType) => {
    const { valueKeys: key, options } = props;
    return getMultipleSelectDefaultValue(
      filterValue,
      key,
      options,
      options.map((item) => item.value),
    );
  },
  backValue: (filterValue: any, props: TSingleSelectType) => {
    const { valueKeys: key, options } = props;
    const list = filterValue[key] || [];

    // 全选：返回空数组
    if (list.length === options.length && list.length > 0) {
      return { [key]: [] };
    }

    // 全不选：返回 ["-99999"]
    if (list.length === 0) {
      return { [key]: ['-99999'] };
    }

    // 部分选择：返回选中的 channels
    return { [key]: list };
  },
  condDealwith: dealwithCondOptions,
  requestKey: 'materials_config_channels',
  props: {
    title: 'Channel',
    titleCn: '渠道',
    valueKeys: 'channels',
    itemRender: (item: any) => (
      <ChannelLabel
        iconStyle={{ width: 18, height: 18 }}
        channel_name={item?.value as 'tiktok'}
        isMarketing
      />
    ),
    options: [],
    searchable: true,
    listHeight: 300,
  },
};

export const ADFirstSeenDateSelect: TComponentConfig<TMultiDateAllSelect> = {
  Component: MultiDateAllSelect,
  props: {
    valueKeys: {
      start_time: 'first_seen_start',
      end_time: 'first_seen_end',
    },
    optionsMap: {
      date: { value: 'date', text: 'First Seen', text_cn: '首次发现' },
    },
  },
  defaultValue: (filterValue: any, props: IDateAllCommonSelectType) => {
    return getAllDateDefaultValue(filterValue, props);
  },
  backValue: dateBackValueFunc,
};

export const ADLastSeenDateSelect: TComponentConfig<TMultiDateAllSelect> = {
  Component: MultiDateAllSelect,
  props: {
    valueKeys: {
      start_time: 'last_seen_start',
      end_time: 'last_seen_end',
    },
    optionsMap: {
      date: { value: 'date', text: 'Last Seen', text_cn: '最后发现' },
    },
  },
  defaultValue: (filterValue: any, props: IDateAllCommonSelectType) => {
    return getAllDateDefaultValue(filterValue, props);
  },
  backValue: dateBackValueFunc,
};

export const ADCountryRegion: TComponentConfig<TRegionSelectType> = {
  Component: RegionSelect,
  defaultValue: (filterValue: any, props: TRegionSelectType) => {
    const { valueKeys: key, options } = props;
    return getMultipleSelectDefaultValueWithSpecialCase(
      filterValue,
      key,
      options,
      [],
    );
  },
  backValue: (filterValue: any, props: TRegionSelectType) => {
    const key = props.valueKeys;
    const value = filterValue[key];
    const markets = value?.markets || [];
    // 计算所有可用的二级 markets
    const allMarkets: string[] = [];
    (props.options || []).forEach((region: any) => {
      (region.children || []).forEach((child: any) => {
        allMarkets.push(child.value);
      });
    });

    // 全选：返回空数组（去重后比较）
    const uniqueMarkets = Array.from(new Set(markets));
    const uniqueAllMarkets = Array.from(new Set(allMarkets));
    if (
      uniqueMarkets.length === uniqueAllMarkets.length &&
      uniqueMarkets.length > 0
    ) {
      return { [key]: [] };
    }

    // 全不选：返回 ["-99999"]
    if (markets.length === 0) {
      return { [key]: ['-99999'] };
    }

    // 部分选择：返回选中的 markets
    return { [key]: markets };
  },
  condDealwith: (condInfo: any, key: string) => {
    const data = condInfo[key];
    if (!data) return [];

    // 兼容两种数据结构：
    // 1. 直接是数组: [{ region_code, region_en, region_zh, countries: [...] }]
    // 2. 嵌套在对象中: { region_country: [...] }
    const regions = Array.isArray(data)
      ? data
      : data.region_country || data.countries || [];

    // 转换为 TSecondOptionsItem 二级结构
    return (regions || []).map(
      ({ region_code, region_en, region_zh, countries }: any) => {
        return {
          value: region_code?.toLocaleLowerCase?.(),
          text: region_en,
          textCN: region_zh,
          children: (countries || []).map(
            ({ country_code, country_en, country_zh }: any) => {
              const countryKey = country_en?.toLowerCase?.();

              return {
                value: country_code?.toLocaleLowerCase?.(),
                text: countryEn[countryKey] || country_en,
                textCN: countryCn[countryKey] || country_zh,
                code: country_en,
              };
            },
          ),
        };
      },
    );
  },
  requestKey: 'materials_config_region_countries',
  props: {
    title: 'Country/Region',
    titleCn: '国家/地区',
    valueKeys: 'countries',
    options: [],
    hasGlobal: false,
    maxDisplayCount: 1,
  },
};